import React, { useEffect, useState } from "react";
import { config } from "../config/environment";
import { useCheckResult } from "../hooks/useApi";

interface ResultScreenProps {
  exid: string;
  timestamp: string;
}

const ResultScreen: React.FC<ResultScreenProps> = ({ exid, timestamp }) => {
  const [countdown, setCountdown] = useState(20);
  const { data, isLoading, error } = useCheckResult(exid, timestamp);

  // Redirection automatique si le statut est 'ok'
  useEffect(() => {
    if (data && data.status === "ok") {
      console.log("Statut OK, redirection vers:", config.SUCCESS_REDIRECT_URL);

      // Décompte de 20 secondes
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.href = config.SUCCESS_REDIRECT_URL;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }

    if (data && data.status === "error") {
      console.log("Statut KO, redirection vers:", config.REDIRECT_URL);
      // Délai de 2 secondes pour permettre à l'utilisateur de voir le résultat
      const redirectTimer = setTimeout(() => {
        window.location.href = config.REDIRECT_URL;
      }, 5000);

      return () => clearTimeout(redirectTimer);
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="result-screen">
        <h2>Vérification en cours...</h2>
        <p>Veuillez patienter pendant que nous vérifions votre identité.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="result-screen">
        <h2>Erreur</h2>
        <p style={{ color: "red" }}>
          Une erreur s'est produite:{" "}
          {error instanceof Error ? error.message : "Erreur inconnue"}
        </p>
      </div>
    );
  }

  if (data?.error?.code) {
    switch (data?.error?.code) {
      case "no_result_found":
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>Une erreur est survenue, merci de réessayer à nouveau.</p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href={config.REDIRECT_URL}>cliquer ici</a>
            </p>
          </div>
        );
      case "full_name_match":
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>
              Votre prénom ou nom ne correspond pas à celui de votre compte.
            </p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href={config.REDIRECT_URL}>cliquer ici</a>
            </p>
          </div>
        );
      case "no_name_found":
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>Une erreur est survenue, merci de réessayer à nouveau.</p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href={config.REDIRECT_URL}>cliquer ici</a>
            </p>
          </div>
        );
      case "no_valid_license":
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>
              La pièce d'identité ne semble pas respecter la limite d'âge
              requise pour cette offre.
            </p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href={config.REDIRECT_URL}>cliquer ici</a>
            </p>
          </div>
        );
      case "no_birth_date_found":
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>
              La pièce d'identité ne semble pas contenir de date de naissance,
              veuillez réessayer avec un autre document.
            </p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href={config.REDIRECT_URL}>cliquer ici</a>
            </p>
          </div>
        );
      case "document_already_linked":
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>La pièce d'identité a déjà été utilisée.</p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href="config.REDIRECT_URL">cliquer ici</a>
            </p>
          </div>
        );
      default:
        return (
          <div className="error-screen">
            <h2>Erreur</h2>
            <p>Une erreur est survenue, merci de réessayer à nouveau.</p>
            <p>
              Vous allez être redirigé vers Ligue1+ dans quelques secondes...
            </p>
            <p>
              Si vous n'êtes pas redirigé automatiquement merci de{" "}
              <a href="config.REDIRECT_URL">cliquer ici</a>
            </p>
          </div>
        );
    }
  }

  return (
    <div className="result-screen">
      <h2>Résultat de la vérification</h2>

      {data && data.status === "ok" && (
        <div className="success-message">
          <h3 style={{ color: "green" }}>✅ Vérification réussie !</h3>
          <p>
            Nous activons votre compte sur Ligue1+, nous vous redirigerons dans
            quelques secondes...
          </p>
          <div className="loader">
            <div className="spinner-container">
              <div className="spinner"></div>
              <div className="countdown-overlay">{countdown}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultScreen;
